import React, { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useAuthContext } from './AuthContext';
import { useAppSnackbar } from '../components/useAppSnackbar';
import { appId } from '../branding';
import { useLocation } from 'react-router-dom';
import { menus } from '../routes';
import { useMediaQuery } from 'react-responsive';
import { Star } from 'lucide-react';

type ThemeName = 'light' | 'dark';

interface AppContextProps {
    shortMenu: boolean;
    isScrolled: boolean;
    loadingApp: boolean;
    subMenu: any;
    setSubMenu: any;
    toggleShortMenu: () => void;
    setShortMenuValue: (value: boolean) => void;
    theme: ThemeName;
    toggleTheme: () => void;
    successSnackbar: any;
    errorSnackbar: any;
    showDrawerMenu: any;
    toggleShowDrawerMenu: any;
    image: string;
    reloadSubMenu: any;
    hiddenValue: any;
    setHiddenValue: any;
    triggerPlanCelebration: (content?: PlanCelebrationContent) => void;
}

const AppContext = createContext<AppContextProps | undefined>(undefined);

type PlanCelebrationContent = {
    title?: string;
    message?: string;
};

const DEFAULT_PLAN_CELEBRATION = {
    title: 'Plano ativado',
    message: 'Tudo certo com a cobrança. Seu acesso já foi liberado.',
};

const FIREWORK_BURSTS = [
    { left: '21%', top: '28%', color: '#f97316', delay: 0 },
    { left: '50%', top: '20%', color: '#facc15', delay: 0.12 },
    { left: '78%', top: '31%', color: '#38bdf8', delay: 0.24 },
    { left: '33%', top: '58%', color: '#fb7185', delay: 0.34 },
    { left: '68%', top: '62%', color: '#22c55e', delay: 0.44 },
] as const;

const FIREWORK_COLORS = ['#f97316', '#facc15', '#fb7185', '#38bdf8', '#22c55e', '#a855f7'] as const;

const FIREWORK_ROCKETS = [
    { left: '18%', height: '44vh', color: '#f97316', delay: 0 },
    { left: '48%', height: '55vh', color: '#facc15', delay: 0.08 },
    { left: '80%', height: '46vh', color: '#38bdf8', delay: 0.18 },
] as const;

const PlanActivationCelebration = ({
    visible,
    content,
}: {
    visible: boolean;
    content: Required<PlanCelebrationContent>;
}) => (
    <AnimatePresence>
        {visible && (
            <motion.div
                className="pointer-events-none fixed inset-0 z-[9999] overflow-hidden"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
            >
                <motion.div
                    className="absolute inset-0 bg-slate-950/15 backdrop-blur-[1px]"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: [0, 1, 1, 0] }}
                    transition={{ duration: 4.2, times: [0, 0.1, 0.82, 1], ease: 'easeOut' }}
                />

                {FIREWORK_ROCKETS.map((rocket) => (
                    <motion.span
                        key={`${rocket.left}-${rocket.height}`}
                        className="absolute bottom-0 block w-[3px] rounded-full"
                        style={{
                            left: rocket.left,
                            height: rocket.height,
                            background: `linear-gradient(to top, transparent, ${rocket.color}, white)`,
                            boxShadow: `0 0 28px ${rocket.color}`,
                        }}
                        initial={{ y: '24vh', opacity: 0 }}
                        animate={{ y: ['24vh', '-10vh'], opacity: [0, 1, 0] }}
                        transition={{ duration: 1.05, delay: rocket.delay, ease: 'easeOut' }}
                    />
                ))}

                {FIREWORK_BURSTS.map((burst, burstIndex) => (
                    <div key={`${burst.left}-${burst.top}`} className="absolute" style={{ left: burst.left, top: burst.top }}>
                        <motion.span
                            className="absolute block h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full"
                            style={{
                                backgroundColor: burst.color,
                                boxShadow: `0 0 32px ${burst.color}`,
                            }}
                            initial={{ opacity: 0, scale: 0.1 }}
                            animate={{ opacity: [0, 1, 0], scale: [0.1, 1.45, 0.4] }}
                            transition={{ duration: 1.4, delay: burst.delay, ease: 'easeOut' }}
                        />

                        {Array.from({ length: 18 }).map((_, particleIndex) => {
                            const angle = (Math.PI * 2 * particleIndex) / 18;
                            const distance = 72 + ((particleIndex + burstIndex) % 4) * 24;
                            const x = Math.cos(angle) * distance;
                            const y = Math.sin(angle) * distance;
                            const color = FIREWORK_COLORS[(particleIndex + burstIndex) % FIREWORK_COLORS.length];

                            return (
                                <motion.span
                                    key={`${burst.left}-${particleIndex}`}
                                    className="absolute block h-2.5 w-2.5 rounded-full"
                                    style={{
                                        backgroundColor: color,
                                        boxShadow: `0 0 18px ${color}`,
                                    }}
                                    initial={{ x: 0, y: 0, opacity: 0, scale: 0.2 }}
                                    animate={{
                                        x,
                                        y,
                                        opacity: [0, 1, 1, 0],
                                        scale: [0.2, 1, 0.65, 0],
                                    }}
                                    transition={{
                                        duration: 1.65,
                                        delay: burst.delay + 0.08,
                                        ease: [0.12, 0.8, 0.25, 1],
                                    }}
                                />
                            );
                        })}
                    </div>
                ))}

                <div className="absolute inset-0 flex items-center justify-center px-4">
                    <motion.div
                        className="w-[min(92vw,34rem)]"
                        initial={{ opacity: 0, y: 30, scale: 0.86 }}
                        animate={{ opacity: [0, 1, 1, 0], y: [30, 0, 0, -20], scale: [0.86, 1.04, 1, 0.96] }}
                        exit={{ opacity: 0, y: -20, scale: 0.96 }}
                        transition={{ duration: 4.2, times: [0, 0.14, 0.74, 1], ease: 'easeOut' }}
                    >
                        <div className="rounded-[2rem] border border-white/70 bg-gradient-to-br from-amber-50 via-white to-sky-50 px-7 py-6 text-center shadow-[0_36px_120px_rgba(15,23,42,0.35)]">
                            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-amber-400 via-orange-400 to-rose-500 text-white shadow-[0_18px_45px_rgba(249,115,22,0.38)]">
                                <Star className="text-xl fill-current" aria-hidden="true" />
                            </div>
                            <div className="mt-4 text-2xl font-semibold text-slate-950">{content.title}</div>
                            <div className="mt-2 text-sm text-slate-600">{content.message}</div>
                        </div>
                    </motion.div>
                </div>
            </motion.div>
        )}
    </AnimatePresence>
);

const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const isCompactMenu = useMediaQuery({ maxWidth: 1024 });

    const { loadingAuth } = useAuthContext();
    const [loadingApp, setLoadingApp] = useState(true);
    const { user } = useAuthContext();

    const [shortMenu, setShortMenu] = useState(localStorage.getItem('shortMenu') === 'true');
    const [isScrolled, setIsScrolled] = useState(false);
    const [subMenu, setSubMenu] = useState(false);
    const [theme, setTheme] = useState<ThemeName>(() => {
        if (typeof window === 'undefined') {
            return 'light';
        }

        const stored = window.localStorage.getItem('theme');
        if (stored === 'light' || stored === 'dark') {
            return stored;
        }

        return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    });
    const [hiddenValue, setHiddenValue] = useState<boolean>(() => {
        const saved = localStorage.getItem('hiddenValue');
        return saved ? JSON.parse(saved) : false;
    });
    const [planCelebrationVisible, setPlanCelebrationVisible] = useState(false);
    const [planCelebrationContent, setPlanCelebrationContent] = useState(DEFAULT_PLAN_CELEBRATION);
    const planCelebrationTimeoutRef = useRef<number | null>(null);

    useEffect(() => {
        localStorage.setItem('hiddenValue', JSON.stringify(hiddenValue));
    }, [hiddenValue]);
    const [showDrawerMenu, setShowDrawerMenu] = useState(false);

    const [image] = useState(`${appId}/logo.png`);

    const location = useLocation();

    const reloadSubMenu = () => {
        const menu = menus(user).find(
            ({ path, title }) => path === location.pathname || (title === 'Agenda' && location.pathname === '/agenda'),
        );

        if (menu?.title === 'Agenda' && new URLSearchParams(location.search).get('action') === 'add') {
            setSubMenu(false);
            return;
        }

        setSubMenu(menu?.subMenu);
    };

    useEffect(() => {
        if (!!user) {
            reloadSubMenu();
        }
    }, [location, user]);

    useEffect(() => {
        if (isCompactMenu) {
            setShortMenu(true);
            return;
        }

        const stored = localStorage.getItem('shortMenu');
        if (stored === null) {
            setShortMenu(false);
            return;
        }

        setShortMenu(stored === 'true');
    }, [isCompactMenu]);

    useEffect(() => {
        if (typeof document === 'undefined') {
            return;
        }

        document.documentElement.setAttribute('data-theme', theme);
        document.documentElement.classList.toggle('dark', theme === 'dark');
        document.documentElement.style.colorScheme = theme;
        // Barra do navegador no celular acompanha o fundo do app (base-200 de cada tema).
        document.querySelector('meta[name="theme-color"]')?.setAttribute('content', theme === 'dark' ? '#101012' : '#f4f7fb');
        window.localStorage.setItem('theme', theme);
    }, [theme]);

    const { successSnackbar, errorSnackbar } = useAppSnackbar();

    const toggleShortMenu = () => {
        localStorage.setItem('shortMenu', (!shortMenu).toString());
        setShortMenu(!shortMenu);
    };

    const setShortMenuValue = (value: boolean) => {
        localStorage.setItem('shortMenu', value.toString());
        setShortMenu(value);
    };

    const toggleTheme = () => {
        setTheme((current) => (current === 'dark' ? 'light' : 'dark'));
    };

    const toggleShowDrawerMenu = () => {
        setShowDrawerMenu((current) => !current);
    };

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 20);
        };

        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    useEffect(() => {
        setLoadingApp(loadingAuth);
    }, [loadingAuth]);

    useEffect(() => {
        setShowDrawerMenu(false);
    }, [location.pathname]);

    useEffect(() => {
        return () => {
            if (planCelebrationTimeoutRef.current) {
                window.clearTimeout(planCelebrationTimeoutRef.current);
            }
        };
    }, []);

    const triggerPlanCelebration = useCallback((content: PlanCelebrationContent = {}) => {
        setPlanCelebrationContent({
            title: content.title || DEFAULT_PLAN_CELEBRATION.title,
            message: content.message || DEFAULT_PLAN_CELEBRATION.message,
        });
        setPlanCelebrationVisible(false);

        if (planCelebrationTimeoutRef.current) {
            window.clearTimeout(planCelebrationTimeoutRef.current);
        }

        window.requestAnimationFrame(() => {
            setPlanCelebrationVisible(true);
            planCelebrationTimeoutRef.current = window.setTimeout(() => {
                setPlanCelebrationVisible(false);
                planCelebrationTimeoutRef.current = null;
            }, 4400);
        });
    }, []);

    return (
        <AppContext.Provider
            value={{
                shortMenu,
                loadingApp,
                isScrolled,
                toggleShortMenu,
                setShortMenuValue,
                theme,
                toggleTheme,
                successSnackbar,
                errorSnackbar,
                showDrawerMenu,
                toggleShowDrawerMenu,
                image,
                subMenu,
                setSubMenu,
                reloadSubMenu,
                hiddenValue,
                setHiddenValue,
                triggerPlanCelebration,
            }}
        >
            <PlanActivationCelebration visible={planCelebrationVisible} content={planCelebrationContent} />
            {children}
        </AppContext.Provider>
    );
};

const useAppContext = () => {
    const context = useContext(AppContext);
    if (context === undefined) {
        throw new Error('useAppContext must be used within an AppProvider');
    }
    return context;
};

export { AppProvider, useAppContext };
